// src/components/Statistiques.js
import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, where, Timestamp } from "firebase/firestore";
import { db } from "../services/firebase";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

export default function Statistiques() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenusParMois, setRevenusParMois] = useState([]);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [periode, setPeriode] = useState("tout");

  useEffect(() => { charger(); }, []);

  function appliquerPeriode(p) {
    const now = new Date();
    setPeriode(p);
    if (p === "7j") {
      const d = new Date(); d.setDate(d.getDate() - 7);
      setDateDebut(d.toISOString().split("T")[0]);
      setDateFin(now.toISOString().split("T")[0]);
    } else if (p === "30j") {
      const d = new Date(); d.setDate(d.getDate() - 30);
      setDateDebut(d.toISOString().split("T")[0]);
      setDateFin(now.toISOString().split("T")[0]);
    } else if (p === "3m") {
      const d = new Date(); d.setMonth(d.getMonth() - 3);
      setDateDebut(d.toISOString().split("T")[0]);
      setDateFin(now.toISOString().split("T")[0]);
    } else if (p === "année") {
      setDateDebut(`${now.getFullYear()}-01-01`);
      setDateFin(`${now.getFullYear()}-12-31`);
    } else {
      setDateDebut("");
      setDateFin("");
    }
  }

  async function charger() {
    try {
      const orgsSnap = await getDocs(collection(db, "organisations"));
      const orgs = orgsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const paiementsSnap = await getDocs(query(collection(db, "paiements"), orderBy("createdAt", "desc")));
      const paiements = paiementsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const totalOrgs = orgs.length;
      const totalRevenu = paiements.reduce((acc, p) => acc + (p.amount || 0), 0);
      const totalPaiements = paiements.length;

      let totalTombolas = 0;
      let totalBillets = 0;
      let tombolesPro = 0;
      let tombolesCeMois = 0;
      const now = new Date();

      for (const org of orgs) {
        const tombolasSnap = await getDocs(collection(db, "organisations", org.id, "tombolas"));
        totalTombolas += tombolasSnap.size;
        for (const t of tombolasSnap.docs) {
          if (t.data().plan === "pro") tombolesPro++;
          const createdAt = t.data().createdAt?.toDate?.();
          if (createdAt && createdAt.getMonth() === now.getMonth() && createdAt.getFullYear() === now.getFullYear()) {
            tombolesCeMois++;
          }
          const billetsSnap = await getDocs(collection(db, "organisations", org.id, "tombolas", t.id, "billets"));
          totalBillets += billetsSnap.size;
        }
      }

      // Revenus par mois
      const parMois = {};
      paiements.forEach((p) => {
        if (p.createdAt?.toDate) {
          const d = p.createdAt.toDate();
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          const label = d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
          if (!parMois[key]) parMois[key] = { mois: key, label, revenu: 0, paiements: 0 };
          parMois[key].revenu += p.amount || 0;
          parMois[key].paiements++;
        }
      });

      setRevenusParMois(Object.values(parMois).sort((a, b) => a.mois.localeCompare(b.mois)));
      setStats({ totalOrgs, totalRevenu, totalPaiements, totalTombolas, totalBillets, tombolesPro, tombolesCeMois });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  // Filtrer les données par date
  const revenusFiltrés = revenusParMois.filter((r) => {
    if (!dateDebut && !dateFin) return true;
    const moisDate = new Date(r.mois + "-01");
    let ok = true;
    if (dateDebut) ok = ok && moisDate >= new Date(dateDebut.slice(0, 7) + "-01");
    if (dateFin) ok = ok && moisDate <= new Date(dateFin.slice(0, 7) + "-01");
    return ok;
  });

  const revenuFiltre = revenusFiltrés.reduce((acc, r) => acc + r.revenu, 0);
  const paiementsFiltrés = revenusFiltrés.reduce((acc, r) => acc + r.paiements, 0);

  if (loading) return <div style={s.loading}>⏳ Chargement des statistiques…</div>;

  const kpis = [
    { label: "Organisations", value: stats.totalOrgs, icon: "🏢", color: "#1a3a8f" },
    { label: "Tombolas Pro", value: stats.tombolesPro, icon: "⭐", color: "#ff8f00" },
    { label: "Revenu total", value: `${stats.totalRevenu}€`, icon: "💰", color: "#2e7d32" },
    { label: "Paiements", value: stats.totalPaiements, icon: "💳", color: "#764ba2" },
    { label: "Tombolas créées", value: stats.totalTombolas, icon: "🎟️", color: "#0099cc" },
    { label: "Ce mois", value: stats.tombolesCeMois, icon: "📅", color: "#e53935" },
    { label: "Billets vendus", value: stats.totalBillets, icon: "🎫", color: "#00897b" },
    { label: "Taux Pro", value: stats.totalTombolas > 0 ? `${Math.round((stats.tombolesPro / stats.totalTombolas) * 100)}%` : "0%", icon: "📈", color: "#6d4c41" },
  ];

  return (
    <div>
      {/* KPI Grid */}
      <div style={s.kpiGrid}>
        {kpis.map((kpi) => (
          <div key={kpi.label} style={s.kpiCard}>
            <div style={s.kpiIcon}>{kpi.icon}</div>
            <div style={{ ...s.kpiValue, color: kpi.color }}>{kpi.value}</div>
            <div style={s.kpiLabel}>{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Filtres par date */}
      <div style={s.chartCard}>
        <div style={s.chartHeader}>
          <h3 style={s.chartTitle}>📈 Revenus par mois (€)</h3>
          <div style={s.filtres}>
            {["7j", "30j", "3m", "année", "tout"].map((p) => (
              <button key={p} onClick={() => appliquerPeriode(p)}
                style={{ ...s.btnPeriode, ...(periode === p ? s.btnPeriodeActive : {}) }}>
                {p === "tout" ? "Tout" : p === "année" ? "Cette année" : p}
              </button>
            ))}
            <input type="date" value={dateDebut} onChange={(e) => { setDateDebut(e.target.value); setPeriode("custom"); }} style={s.dateInput} />
            <span style={{ color: "#aaa" }}>→</span>
            <input type="date" value={dateFin} onChange={(e) => { setDateFin(e.target.value); setPeriode("custom"); }} style={s.dateInput} />
          </div>
        </div>

        {/* KPI période filtrée */}
        <div style={s.kpiPeriode}>
          <div style={s.kpiPeriodeItem}>
            <span style={s.kpiPeriodeVal}>{revenuFiltre}€</span>
            <span style={s.kpiPeriodeLab}>Revenu période</span>
          </div>
          <div style={s.kpiPeriodeItem}>
            <span style={{ ...s.kpiPeriodeVal, color: "#764ba2" }}>{paiementsFiltrés}</span>
            <span style={s.kpiPeriodeLab}>Paiements période</span>
          </div>
          <div style={s.kpiPeriodeItem}>
            <span style={{ ...s.kpiPeriodeVal, color: "#2e7d32" }}>
              {paiementsFiltrés > 0 ? Math.round(revenuFiltre / paiementsFiltrés) : 0}€
            </span>
            <span style={s.kpiPeriodeLab}>Panier moyen</span>
          </div>
        </div>

        {revenusFiltrés.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenusFiltrés}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [`${v}€`, "Revenu"]} />
              <Bar dataKey="revenu" fill="#667eea" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={s.emptyChart}>Aucune donnée pour cette période.</div>
        )}
      </div>

      {/* Graphique paiements */}
      {revenusFiltrés.length > 1 && (
        <div style={s.chartCard}>
          <h3 style={s.chartTitle}>💳 Nombre de paiements par mois</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={revenusFiltrés}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => [v, "Paiements"]} />
              <Line type="monotone" dataKey="paiements" stroke="#764ba2" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const s = {
  loading: { textAlign: "center", padding: "3rem", color: "#667eea" },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: "16px", marginBottom: "2rem" },
  kpiCard: { background: "#fff", borderRadius: "12px", padding: "1.25rem", textAlign: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #eee" },
  kpiIcon: { fontSize: "1.75rem", marginBottom: "8px" },
  kpiValue: { fontSize: "1.75rem", fontWeight: "900", letterSpacing: "-0.5px" },
  kpiLabel: { fontSize: "0.75rem", color: "#888", fontWeight: "600", marginTop: "4px", textTransform: "uppercase" },
  chartCard: { background: "#fff", borderRadius: "12px", padding: "1.5rem", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", border: "1px solid #eee", marginBottom: "1.5rem" },
  chartHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", flexWrap: "wrap", gap: "12px" },
  chartTitle: { fontSize: "1rem", fontWeight: "700", color: "#1a1a2e", margin: 0 },
  filtres: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" },
  btnPeriode: { padding: "4px 10px", background: "#f0f0f0", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.78rem", fontWeight: "600", color: "#555" },
  btnPeriodeActive: { background: "#1a3a8f", color: "#fff", border: "1px solid #1a3a8f" },
  dateInput: { padding: "4px 8px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.82rem", outline: "none" },
  kpiPeriode: { display: "flex", gap: "16px", marginBottom: "1.25rem", flexWrap: "wrap" },
  kpiPeriodeItem: { display: "flex", flexDirection: "column", alignItems: "center", background: "#f7f8fc", borderRadius: "8px", padding: "0.75rem 1.25rem", flex: 1, minWidth: "100px" },
  kpiPeriodeVal: { fontSize: "1.4rem", fontWeight: "900", color: "#1a3a8f" },
  kpiPeriodeLab: { fontSize: "0.72rem", color: "#888", textTransform: "uppercase", fontWeight: "600", marginTop: "4px" },
  emptyChart: { textAlign: "center", padding: "2rem", color: "#aaa" },
};
